import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { theme, responsiveContainer, createGradient } from '../styles/theme';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    website: '' // Honeypot field for spam protection (bots love filling website fields)
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input.trim(), { 
      ALLOWED_TAGS: [], 
      ALLOWED_ATTR: [] 
    });
  };

  const checkRateLimit = () => {
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const rateLimitKey = 'bytebot_contact_submissions';
    
    try {
      const stored = localStorage.getItem(rateLimitKey);
      const submissions = stored ? JSON.parse(stored) : [];
      
      // Remove submissions older than 24 hours
      const recentSubmissions = submissions.filter(timestamp => now - timestamp < oneDay);
      
      // Check if user has exceeded limit (5 per day)
      if (recentSubmissions.length >= 5) {
        return false;
      }
      
      // Add current timestamp and save
      recentSubmissions.push(now);
      localStorage.setItem(rateLimitKey, JSON.stringify(recentSubmissions));
      
      return true;
    } catch (error) {
      // If localStorage fails, allow the submission
      console.warn('Rate limit check failed:', error);
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Check honeypot field (should be empty)
      if (formData.website !== '') {
        console.log('Spam detected - website field filled');
        setIsSubmitting(false);
        return; // Silently reject spam
      }

      // Check client-side rate limit
      if (!checkRateLimit()) {
        setSubmitError('You have reached the daily limit of 5 messages. Please try again tomorrow.');
        setIsSubmitting(false);
        return;
      }

      // Sanitize all form inputs
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        company: sanitizeInput(formData.company),
        message: sanitizeInput(formData.message)
      };

      // Validate required fields after sanitization
      if (!sanitizedData.name || !sanitizedData.email || !sanitizedData.message) {
        setSubmitError('Please fill in all required fields.');
        setIsSubmitting(false);
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(sanitizedData.email)) {
        setSubmitError('Please enter a valid email address.');
        setIsSubmitting(false);
        return;
      }

      // Prepare template data for the contact form template
      const templateData = {
        name: sanitizedData.name,
        email: sanitizedData.email,
        message: sanitizedData.message,
        company: sanitizedData.company || undefined // Only include if provided
      };

      // Call Firebase function using the contactForm template
      const response = await fetch('https://sendemailrequest-fgliwr5msq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'support@bytesizeds.com',
          templateName: 'contactForm',
          templateData: templateData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const result = await response.json();
      console.log('Email sent successfully:', result);
      
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
              setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', company: '', message: '', website: '' });
        }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
    },
    
    // Enhanced Hero Section
    hero: {
      background: createGradient(theme.colors.primary, theme.colors.primaryDark, '135deg').background,
      color: '#fff',
      padding: '120px 20px 80px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Crect x='11' y='11' width='4' height='4'/%3E%3Crect x='51' y='11' width='4' height='4'/%3E%3Crect x='11' y='51' width='4' height='4'/%3E%3Crect x='51' y='51' width='4' height='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    heroContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    heroTitle: {
      fontSize: theme.typography.fontSizes['6xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.1',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    heroSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['3xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['3xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
    },

    // Main Content Section
    content: {
      padding: '120px 20px',
      backgroundColor: theme.colors.surface,
    },
    
    contentContainer: {
      ...responsiveContainer,
    },
    
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: theme.spacing['4xl'],
      alignItems: 'start',
    },

    // Enhanced Contact Form
    contactForm: {
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['3xl'],
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.shadows.lg,
    },
    
    formGlow: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${theme.colors.primary}05, transparent)`,
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    
    formTitle: {
      fontSize: theme.typography.fontSizes['3xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing['2xl'],
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      position: 'relative',
    },
    
    formTitleUnderline: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: 0,
      width: '60px',
      height: '4px',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.borderRadius.full,
    },
    
    formGroup: {
      marginBottom: theme.spacing.xl,
      position: 'relative',
    },
    
    label: {
      display: 'block',
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    input: {
      width: '100%',
      padding: theme.spacing.lg,
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
    },
    
    inputFocus: {
      borderColor: theme.colors.primary,
      boxShadow: `0 0 0 3px ${theme.colors.primary}20`,
      outline: 'none',
    },
    
    textarea: {
      width: '100%',
      padding: theme.spacing.lg,
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
      minHeight: '160px',
      resize: 'vertical',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      lineHeight: '1.6',
    },
    
    submitButton: {
      width: '100%',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      position: 'relative',
      overflow: 'hidden',
    },
    
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },

    // Contact Information Cards - Made smaller
    contactInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    
    infoCard: {
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    
    infoIcon: {
      fontSize: theme.typography.fontSizes['5xl'],
      color: theme.colors.primary,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontWeight: theme.typography.fontWeights.bold,
      textShadow: `0 0 20px ${theme.colors.primary}40`,
    },
    
    infoTitle: {
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    infoText: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      lineHeight: '1.6',
      marginBottom: theme.spacing.sm,
    },
    
    infoEmail: {
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.semibold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textDecoration: 'none',
      transition: 'color 0.2s ease',
    },

    // Success Message
    successMessage: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: theme.spacing.xl,
      borderRadius: theme.borderRadius.lg,
      textAlign: 'center',
      marginBottom: theme.spacing.xl,
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.semibold,
      border: `2px solid ${theme.colors.primary}`,
      position: 'relative',
      overflow: 'hidden',
    },
    
    successPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M20 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0-20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z'/%3E%3C/g%3E%3C/svg%3E")`,
    },

    // Error Message
    errorMessage: {
      backgroundColor: '#dc3545',
      color: '#fff',
      padding: theme.spacing.xl,
      borderRadius: theme.borderRadius.lg,
      textAlign: 'center',
      marginBottom: theme.spacing.xl,
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.semibold,
      border: '2px solid #dc3545',
      position: 'relative',
      overflow: 'hidden',
    },



    // Enhanced Final CTA - Match Homepage exactly
    finalCta: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
    },
    
    finalCtaContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    finalCtaTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      marginBottom: theme.spacing.lg,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaText: {
      fontSize: theme.typography.fontSizes.xl,
      marginBottom: theme.spacing['2xl'],
      maxWidth: '600px',
      margin: `0 auto ${theme.spacing['2xl']} auto`,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: '20px 50px',
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Responsive
    '@media (maxWidth: 768px)': {
      heroTitle: {
        fontSize: theme.typography.fontSizes['4xl'],
      },
      grid: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing['2xl'],
      },
      contactForm: {
        padding: theme.spacing['2xl'],
      },
    },
  };

  const handleCardHover = (e, isHovering) => {
    if (isHovering) {
      e.currentTarget.style.transform = 'translateY(-8px)';
      e.currentTarget.style.boxShadow = theme.shadows.xl;
    } else {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }
  };

  const handleFormHover = (e, isHovering) => {
    const glow = e.currentTarget.querySelector('.form-glow');
    if (glow) {
      glow.style.opacity = isHovering ? '1' : '0';
    }
  };

  return (
    <div style={styles.container}>
      {/* Enhanced Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Get In Touch
          </h1>
          <p style={styles.heroSubtitle}>
            Ready to transform your customer support? 
            Let's discuss how ByteBot can help your business succeed with AI.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section style={styles.content}>
        <div style={styles.contentContainer}>
          <div style={styles.grid}>
            {/* Enhanced Contact Form */}
            <div 
              style={styles.contactForm}
              onMouseEnter={(e) => handleFormHover(e, true)}
              onMouseLeave={(e) => handleFormHover(e, false)}
            >
              <div className="form-glow" style={styles.formGlow}></div>
              
              <h2 style={styles.formTitle}>
                [SEND MESSAGE]
                <div style={styles.formTitleUnderline}></div>
              </h2>
              
              {isSubmitted && (
                <div style={styles.successMessage}>
                  <div style={styles.successPattern}></div>
                  <div style={{position: 'relative', zIndex: 1}}>
                    [SUCCESS] Message sent! We'll respond within 24 hours.
                  </div>
                </div>
              )}

              {submitError && (
                <div style={styles.errorMessage}>
                  <div style={{position: 'relative', zIndex: 1}}>
                    [ERROR] {submitError}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="name">[NAME] *</label>
                  <input
                    style={styles.input}
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength={100}
                    required
                    disabled={isSubmitting}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.colors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="email">[EMAIL] *</label>
                  <input
                    style={styles.input}
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    maxLength={254}
                    required
                    disabled={isSubmitting}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.colors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="company">[COMPANY]</label>
                  <input
                    style={styles.input}
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={150}
                    disabled={isSubmitting}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.colors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="message">[MESSAGE] *</label>
                  <textarea
                    style={styles.textarea}
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={2000}
                    placeholder="Tell us about your business and how you'd like to use AI chatbots..."
                    required
                    disabled={isSubmitting}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.colors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Honeypot field - hidden from users */}
                <div style={{display: 'none'}}>
                  <label htmlFor="website">Website URL</label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    maxLength={200}
                    tabIndex="-1"
                    autoComplete="off"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    ...(isSubmitting ? styles.submitButtonDisabled : {})
                  }}
                  disabled={isSubmitting}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = theme.colors.primaryDark;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = theme.shadows.lg;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = theme.colors.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {isSubmitting ? '[SENDING...]' : '[SEND MESSAGE]'}
                </button>
              </form>
            </div>

            {/* Contact Information Cards */}
            <div style={styles.contactInfo}>
              <div 
                style={styles.infoCard}
                onMouseEnter={(e) => handleCardHover(e, true)}
                onMouseLeave={(e) => handleCardHover(e, false)}
              >
                <div style={styles.infoIcon}>[EMAIL]</div>
                <h3 style={styles.infoTitle}>General Support</h3>
                <p style={styles.infoText}>
                  <a href="mailto:support@bytesizeds.com" style={styles.infoEmail}>
                    support@bytesizeds.com
                  </a>
                </p>
                <p style={styles.infoText}>
                  We respond to all inquiries within 24 hours, usually much faster.
                </p>
              </div>

              <div 
                style={styles.infoCard}
                onMouseEnter={(e) => handleCardHover(e, true)}
                onMouseLeave={(e) => handleCardHover(e, false)}
              >
                <div style={styles.infoIcon}>[SALES]</div>
                <h3 style={styles.infoTitle}>Sales & Partnerships</h3>
                <p style={styles.infoText}>
                  <a href="mailto:support@bytesizeds.com" style={styles.infoEmail}>
                    support@bytesizeds.com
                  </a>
                </p>
                <p style={styles.infoText}>
                  Interested in volume discounts or custom solutions? Let's explore how we can work together.
                </p>
              </div>

              <div 
                style={styles.infoCard}
                onMouseEnter={(e) => handleCardHover(e, true)}
                onMouseLeave={(e) => handleCardHover(e, false)}
              >
                <div style={styles.infoIcon}>[SUPPORT]</div>
                <h3 style={styles.infoTitle}>Technical Help</h3>
                <p style={styles.infoText}>
                  <a href="mailto:support@bytesizeds.com" style={styles.infoEmail}>
                    support@bytesizeds.com
                  </a>
                </p>
                <p style={styles.infoText}>
                  Need help with setup, integration, or troubleshooting? Our team is here to help.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Final CTA */}
      <section style={styles.finalCta}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>Questions? Let's Talk</h2>
          <p style={styles.finalCtaText}>
            Ready to see how ByteBot can work for your business?<br/>
            Get in touch and we'll help you get started.
          </p>
          <button 
            style={styles.finalCtaButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0) scale(1)';
            }}
            onClick={() => window.location.href = '/'}
          >
            [Get Started Free]
          </button>
        </div>
      </section>
    </div>
  );
}

export default Contact; 